import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  GripVertical,
  Loader2,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import {
  createEmptyFactor,
  SENTIMENT_OPTIONS,
  SENTIMENT_TYPE_BULLISH,
  type AssetTranslationAggregate,
  type FactorRow,
} from "../../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

export default function TranslationEditor() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ticker = "" } = useParams<{ ticker: string }>();

  const query = useQuery({
    queryKey: ["translations", "detail", ticker],
    queryFn: () => api.translations.get(ticker),
    enabled: !!ticker,
  });

  const [aggregate, setAggregate] = useState<AssetTranslationAggregate | null>(
    null
  );

  useEffect(() => {
    if (query.data) setAggregate(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (body: AssetTranslationAggregate) =>
      api.translations.update(ticker, body),
    onSuccess: () => {
      toast.success("Translation saved");
      void queryClient.invalidateQueries({ queryKey: ["translations"] });
      navigate("/dashboard/translations");
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : "Failed to save translation"
      ),
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (query.isError || !aggregate) {
    return (
      <div className="space-y-4">
        <Link
          to="/dashboard/translations"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to translations
        </Link>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof ApiError
            ? query.error.message
            : "Failed to load translation"}
        </div>
      </div>
    );
  }

  const update = <K extends keyof AssetTranslationAggregate>(
    field: K,
    value: AssetTranslationAggregate[K]
  ) => setAggregate((prev) => (prev ? { ...prev, [field]: value } : prev));

  const updateFactor = (index: number, patch: Partial<FactorRow>) =>
    setAggregate((prev) =>
      prev
        ? {
            ...prev,
            factors: prev.factors.map((f, i) =>
              i === index ? { ...f, ...patch } : f
            ),
          }
        : prev
    );

  const addFactor = () =>
    setAggregate((prev) =>
      prev
        ? { ...prev, factors: [...prev.factors, createEmptyFactor(prev.stockId)] }
        : prev
    );

  const removeFactor = (index: number) =>
    setAggregate((prev) =>
      prev
        ? { ...prev, factors: prev.factors.filter((_, i) => i !== index) }
        : prev
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (aggregate) save.mutate(aggregate);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" type="button">
          <Link to="/dashboard/translations">
            <ArrowLeft className="h-4 w-4" /> Back to list
          </Link>
        </Button>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save changes
        </Button>
      </div>

      <Card>
        <div className="mb-4 flex items-center gap-2">
          <h3 className="font-semibold">
            Asset{aggregate.ticker ? ` (${aggregate.ticker})` : ""}
          </h3>
          {aggregate.isEtf && (
            <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
              ETF
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Input
            type="number"
            value={aggregate.stockId || ""}
            disabled
            className="max-w-xs font-mono text-lg font-semibold"
          />
          {aggregate.ticker && (
            <span className="font-mono text-lg font-semibold text-muted-foreground">
              {aggregate.ticker}
            </span>
          )}
        </div>
      </Card>

      <Card title="Company name (Hebrew)">
        <Input
          dir="rtl"
          value={aggregate.companyName}
          onChange={(e) => update("companyName", e.target.value)}
          placeholder="שם החברה בעברית"
          className="text-right"
        />
      </Card>

      <Card title="Company description (Hebrew)">
        <textarea
          value={aggregate.description}
          onChange={(e) => update("description", e.target.value)}
          dir="rtl"
          rows={6}
          placeholder="תיאור החברה בעברית"
          className="w-full rounded-md border border-input bg-background p-3 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </Card>

      <Card title="Income sources (Hebrew)">
        <textarea
          value={aggregate.incomeSources}
          onChange={(e) => update("incomeSources", e.target.value)}
          dir="rtl"
          rows={4}
          placeholder="מקורות ההכנסה בעברית"
          className="w-full rounded-md border border-input bg-background p-3 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </Card>

      <Card title="Bull / Bear summary (Hebrew)">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-green-600">Bullish summary</Label>
            <textarea
              value={aggregate.bullishSummary}
              onChange={(e) => update("bullishSummary", e.target.value)}
              dir="rtl"
              rows={4}
              placeholder="סיכום שורי בעברית"
              className="w-full rounded-md border border-input bg-background p-3 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-red-600">Bearish summary</Label>
            <textarea
              value={aggregate.bearishSummary}
              onChange={(e) => update("bearishSummary", e.target.value)}
              dir="rtl"
              rows={4}
              placeholder="סיכום דובי בעברית"
              className="w-full rounded-md border border-input bg-background p-3 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-semibold">
            {aggregate.isEtf ? "ETF positive/negative factors" : "News keypoints"}
          </h3>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addFactor}
          >
            <Plus className="h-4 w-4" /> Add factor
          </Button>
        </div>

        {aggregate.factors.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No factors yet. Click "Add factor" to create one.
          </p>
        ) : (
          <div className="space-y-3">
            {aggregate.factors.map((factor, index) => (
              <div
                key={index}
                className="rounded-md border bg-muted/30 p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <GripVertical className="h-4 w-4" />
                    <span className="font-medium">#{index + 1}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={factor.sentimentTypeId}
                      onChange={(e) =>
                        updateFactor(index, {
                          sentimentTypeId: parseInt(e.target.value, 10),
                        })
                      }
                      className={`h-8 rounded border border-input bg-background px-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring ${
                        factor.sentimentTypeId === SENTIMENT_TYPE_BULLISH
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {SENTIMENT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFactor(index)}
                      title="Remove"
                      className="hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Topic (optional)</Label>
                  <Input
                    dir="rtl"
                    value={factor.topic ?? ""}
                    onChange={(e) =>
                      updateFactor(index, { topic: e.target.value || null })
                    }
                    placeholder="נושא (אופציונלי)"
                    className="text-right text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Key point</Label>
                  <textarea
                    value={factor.keyPoint ?? ""}
                    onChange={(e) =>
                      updateFactor(index, { keyPoint: e.target.value || null })
                    }
                    dir="rtl"
                    rows={3}
                    placeholder="נקודת מפתח בעברית"
                    className="w-full rounded-md border border-input bg-background p-2 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </form>
  );
}

function Card({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-6">
      {title && <h3 className="mb-4 font-semibold">{title}</h3>}
      {children}
    </div>
  );
}
